import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { pageKeyForPath } from "@/lib/pageVisibilityMap";

/**
 * Returns a Set of page_keys that admins have switched off in the
 * `page_visibility` table. Use to hide menu links for non-admin users.
 * Admins always get an empty set so they can preview/manage hidden pages.
 */
export function useHiddenPageKeys(): Set<string> {
  const isAdmin = useIsAdmin();
  const { data } = useQuery({
    queryKey: ["page-visibility-hidden-keys"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("page_visibility")
        .select("page_key, is_visible");
      if (error) throw error;
      return new Set(
        (data || [])
          .filter((r: any) => r.is_visible === false)
          .map((r: any) => r.page_key as string)
      );
    },
    staleTime: 60_000,
  });
  if (isAdmin) return new Set<string>();
  return data ?? new Set<string>();
}

/** Convenience: returns true if the given URL maps to a hidden page key. */
export function useIsUrlHidden() {
  const hidden = useHiddenPageKeys();
  return (url: string) => {
    const path = url.split("?")[0];
    const key = pageKeyForPath(path);
    return !!key && hidden.has(key);
  };
}